/**
 * The browse console — search + facets over all 720 hymns.
 *
 * TWO MODES, the shape Great Host of Composers settled on:
 *   - nothing active (and what SSR renders): the section spine, i.e. the book's
 *     own table of contents.
 *   - a query or any facet: a ranked, paginated result list.
 *
 * ⚠️ EVERY PIECE OF URL STATE IS APPLIED IN AN EFFECT, NEVER AS A `useState`
 * INITIALIZER. One prerendered file serves every query string — S3/CloudFront hand
 * `?section=advent` the same HTML as the bare page — so seeding first render from
 * the URL makes the client disagree with the server markup, and React throws the
 * prerendered page away and rebuilds it. That happens on exactly the URLs this
 * component's own chips produce. `scripts/ci/prerender-hydration-guard.mjs`
 * enforces the rule and follows taint to a fixpoint, so laundering a param through
 * an intermediate const does not evade it.
 *
 * The corresponding thing the guard CANNOT see, and which review has to: a bare
 * `params.get('x') ? <A/> : <B/>` in JSX would mismatch too. There is none here —
 * every read goes through the effects below.
 */
import { useDeferredValue, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import { HYMNAL_1982, index, pages } from '@/data/hymnal'
import {
  anyFilterActive,
  applyFilters,
  keyFacets,
  meterFacets,
  nextSuggestionIndex,
  searchHymns,
  suggest,
  type Filters,
  type LanguageFilter,
  LANGUAGE_FILTERS,
} from '@/lib/hymnal'
import { Chip, FacetRow } from '@/components/Chip'
import { SectionSpine } from '@/components/SectionSpine'

const BASE = `/hymnal/${HYMNAL_1982.id}`
const PAGE_SIZE = 100

/** Validate a param against its own vocabulary. An unknown or stale value is
 *  DROPPED, not applied, so a link that outlives a renamed facet shows everything
 *  rather than nothing. */
const pick = <T extends string>(value: string, allowed: readonly T[]): T | undefined =>
  (allowed as readonly string[]).includes(value) ? (value as T) : undefined

export function HymnBrowser() {
  const [params, setParams] = useSearchParams()

  const urlQuery = params.get('q') ?? ''
  const urlSection = params.get('section') ?? ''
  const urlMeter = params.get('meter') ?? ''
  const urlKey = params.get('key') ?? ''
  const urlLanguage = params.get('language') ?? ''

  // All three start at the value SSR rendered — empty — and are filled in a frame
  // later by the effects below. See the header.
  const [query, setQuery] = useState('')
  const [filters, setFilters] = useState<Filters>({})
  const [shown, setShown] = useState(PAGE_SIZE)

  const meterOptions = useMemo(() => meterFacets(pages.hymns).slice(0, 15), [])
  const keyOptions = useMemo(() => keyFacets(pages.hymns), [])

  useEffect(() => setQuery(urlQuery), [urlQuery])

  useEffect(() => {
    setFilters({
      section: index.sectionBySlug.has(urlSection) ? urlSection : undefined,
      meter: index.meterBySlug.has(urlMeter) ? urlMeter : undefined,
      key: pick(
        urlKey,
        keyOptions.map((k) => k.value)
      ),
      language: pick<LanguageFilter>(urlLanguage, LANGUAGE_FILTERS),
    })
  }, [urlSection, urlMeter, urlKey, urlLanguage, keyOptions])

  // The text is written back OUT so a search is linkable too. Without this,
  // copying the address mid-search silently drops the words: the chips would
  // travel and the query would not, and the recipient would open a different
  // result set than the one being described.
  //
  // This does NOT reintroduce the hydration hazard above — that one is about
  // SEEDING first render from the URL; this only ever writes outward, on a
  // debounce, and skips when the two already agree, so it cannot fight the input.
  // `replace` so back steps over whole searches rather than one per keystroke.
  useEffect(() => {
    if (query === urlQuery) return
    const timer = setTimeout(() => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          if (query.trim()) next.set('q', query)
          else next.delete('q')
          return next
        },
        { replace: true }
      )
    }, 400)
    return () => clearTimeout(timer)
  }, [query, urlQuery, setParams])

  /** A URL with one facet changed and everything else preserved. */
  const facetHref = (key: keyof Filters, value: string | null) => {
    const next = new URLSearchParams(params)
    if (value) next.set(key, value)
    else next.delete(key)
    const qs = next.toString()
    return `${BASE}${qs ? `?${qs}` : ''}`
  }
  const chipHref = (key: keyof Filters, value: string) => facetHref(key, filters[key] === value ? null : value)

  // The input renders from `query` and stays responsive; everything below renders
  // from `search`, which React may fall behind on. Searching is cheap; painting
  // 100 rows is not, and that cost would otherwise land on a keystroke.
  const search = useDeferredValue(query)
  const stale = search !== query

  const active = search.trim() !== '' || anyFilterActive(filters)

  const results = useMemo(() => {
    if (!active) return []
    const base = search.trim() ? searchHymns(pages.hymns, search) : pages.hymns
    return applyFilters(base, filters)
  }, [active, search, filters])

  const suggestions = useMemo(() => (search.trim() ? suggest(index, search) : []), [search])

  useEffect(() => setShown(PAGE_SIZE), [search, filters])

  // ── Typeahead keyboard navigation ───────────────────────────────────────────
  //
  // The suggestions are LINKS, so Tab has always reached them. That is not the
  // same as being usable: a keyboard user had to tab past the input to read the
  // first suggestion, and every keystroke rebuilt the list under them. ↑/↓ keeps
  // focus in the input — which is where the text is — and moves a highlight.
  //
  // `activeIndex` is an index into `suggestions`, -1 meaning "no suggestion chosen,
  // Enter does nothing special". It resets whenever the list changes, because an
  // index into a list that just changed points at the wrong row.
  const [activeIndex, setActiveIndex] = useState(-1)
  // `dismissed` is what Escape sets. Separate from clearing the query: Escape
  // should hide the overlay without destroying what was typed.
  const [dismissed, setDismissed] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    setActiveIndex(-1)
    setDismissed(false)
  }, [search])

  const open = suggestions.length > 0 && !dismissed
  const activeId = activeIndex >= 0 && suggestions[activeIndex] ? `hymn-suggestion-${activeIndex}` : undefined

  const onSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setDismissed(true)
      setActiveIndex(-1)
      return
    }
    if (!open) return
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      // preventDefault so the caret does not jump to the start/end of the input,
      // which is what an unhandled arrow does in a text field and would make the
      // highlight feel like it is fighting the cursor.
      e.preventDefault()
      const delta = e.key === 'ArrowDown' ? 1 : -1
      setActiveIndex((i) => nextSuggestionIndex(i, delta, suggestions.length))
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      // Only when a suggestion is highlighted. With none chosen, Enter is the
      // ordinary "submit my search" and must not hijack to a random result.
      e.preventDefault()
      navigate(suggestions[activeIndex]!.href)
    } else if (e.key === 'Home' && activeIndex >= 0) {
      e.preventDefault()
      setActiveIndex(0)
    } else if (e.key === 'End' && activeIndex >= 0) {
      e.preventDefault()
      setActiveIndex(suggestions.length - 1)
    }
  }

  const sectionsInScope = useMemo(
    () => (filters.section ? pages.sections.filter((s) => s.slug === filters.section) : pages.sections),
    [filters.section]
  )

  return (
    <>
      <div className="mb-8 rounded-lg border border-border bg-card p-5">
        <label htmlFor="hymn-search" className="mb-2 block text-xs uppercase tracking-[0.15em] text-muted-foreground">
          Search
        </label>
        {/* role="combobox" + aria-activedescendant is the pattern where focus
            STAYS in the input and the highlight moves. The alternative — moving
            real focus into the list — would take the caret away from the text
            being edited, which is the thing the user is still typing. */}
        <input
          id="hymn-search"
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onSearchKeyDown}
          placeholder="Hymn number, first line, tune, person, or season…"
          autoComplete="off"
          role="combobox"
          aria-expanded={open}
          aria-controls="hymn-suggestions"
          aria-autocomplete="list"
          aria-activedescendant={activeId}
          className="w-full rounded-md border border-border bg-background px-3 py-2.5 text-foreground placeholder:text-muted-foreground focus-visible:border-border-strong focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
        />

        {open && (
          <ul
            id="hymn-suggestions"
            role="listbox"
            aria-label="Suggestions"
            className="mt-2 divide-y divide-border rounded-md border border-border"
          >
            {suggestions.map((s, i) => (
              <li
                key={`${s.kind}-${s.href}`}
                id={`hymn-suggestion-${i}`}
                role="option"
                aria-selected={i === activeIndex}
              >
                <Link
                  to={s.href}
                  // The highlight is a background, not a focus ring: focus is
                  // still in the input, so a ring here would draw a second focus
                  // indicator on an element that does not have focus.
                  onMouseEnter={() => setActiveIndex(i)}
                  className={`flex min-h-11 touch-manipulation items-center gap-3 px-3 py-2 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none ${
                    i === activeIndex ? 'bg-surface-raised' : 'hover:bg-surface-raised'
                  }`}
                >
                  <span className="w-16 shrink-0 text-[0.65rem] uppercase tracking-wider text-muted-foreground">
                    {s.kind}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-sm text-foreground">{s.label}</span>
                  {s.detail && <span className="shrink-0 text-xs text-muted-foreground">{s.detail}</span>}
                </Link>
              </li>
            ))}
          </ul>
        )}

        {/* Facets collapse on a phone so the results are not pushed below the
            fold. Kept OPEN by default at every width: a closed <details> hides its
            content from the prerendered HTML's first paint, and there is no
            reliable CSS to force one open at a breakpoint — `sm:open` looks like
            it would and is not a utility at all (verified: it generated nothing in
            the built stylesheet). The summary is the affordance; desktop hides it
            because there is room for the panel either way. */}
        <details className="group mt-5" open>
          <summary className="cursor-pointer text-xs uppercase tracking-wider text-muted-foreground sm:hidden">
            Filters
          </summary>
          <div
            className="mt-3 grid grid-cols-1 gap-y-1.5 sm:grid-cols-[max-content_1fr] sm:items-baseline sm:gap-x-5 sm:gap-y-3"
            role="group"
            aria-label="Filters"
          >
            <FacetRow label="Season">
              {pages.sections
                .filter((s) => s.season)
                .map((s) => (
                  <Chip
                    key={s.slug}
                    label={s.label}
                    href={chipHref('section', s.slug)}
                    on={filters.section === s.slug}
                    count={s.hymns.length}
                  />
                ))}
            </FacetRow>
            <FacetRow label="Metre">
              {meterOptions.map((m) => (
                <Chip
                  key={m.slug}
                  label={m.value}
                  href={chipHref('meter', m.slug!)}
                  on={filters.meter === m.slug}
                  count={m.count}
                />
              ))}
            </FacetRow>
            <FacetRow label="Key">
              {keyOptions.map((k) => (
                <Chip key={k.value} label={k.value} href={chipHref('key', k.value)} on={filters.key === k.value} />
              ))}
            </FacetRow>
            <FacetRow label="Language">
              {LANGUAGE_FILTERS.map((l) => (
                <Chip key={l} label={l} href={chipHref('language', l)} on={filters.language === l} />
              ))}
            </FacetRow>
          </div>
        </details>

        {active && (
          <p className="mt-4 text-sm">
            <Link to={BASE} onClick={() => setQuery('')} className="text-muted-foreground hover:text-foreground">
              Clear all filters
            </Link>
          </p>
        )}
      </div>

      {active ? (
        <div className={`transition-opacity duration-150 ${stale ? 'opacity-60' : ''}`} aria-busy={stale || undefined}>
          <p className="text-sm text-muted-foreground" role="status">
            {results.length} {results.length === 1 ? 'hymn' : 'hymns'}
          </p>
          {results.length === 0 ? (
            <div className="mt-4 space-y-2 text-muted-foreground">
              <p>
                Nothing matches those terms.{' '}
                <Link to={BASE} onClick={() => setQuery('')} className="underline underline-offset-4">
                  Browse the hymnal by section
                </Link>
                .
              </p>
              <p className="text-sm">
                You can search a hymn number, a first line, a tune name, a person, or a season — <em>Advent</em>,{' '}
                <em>272</em>, <em>ST ANNE</em>, <em>Vaughan Williams</em>.
              </p>
            </div>
          ) : (
            <>
              <ul className="mt-4 divide-y divide-border rounded-md border border-border">
                {results.slice(0, shown).map((h) => (
                  <li key={h.slug}>
                    <Link
                      to={`${BASE}/hymn/${h.slug}`}
                      className="flex min-h-11 touch-manipulation gap-3 px-3 py-2 hover:bg-surface-raised focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
                    >
                      <span className="w-9 shrink-0 pt-0.5 text-right font-mono text-xs text-muted-foreground">
                        {h.number}
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm text-foreground">{h.firstLine}</span>
                        <span className="block text-xs text-muted-foreground">
                          {[h.tuneName, h.meter, h.section].filter(Boolean).join(' · ')}
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
              {results.length > shown && (
                <button
                  type="button"
                  onClick={() => setShown((n) => n + PAGE_SIZE)}
                  className="mt-4 min-h-11 touch-manipulation rounded border border-border px-4 text-sm text-muted-foreground hover:border-border-strong hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
                >
                  Show {Math.min(PAGE_SIZE, results.length - shown)} more of {results.length}
                </button>
              )}
            </>
          )}
        </div>
      ) : (
        <section aria-labelledby="spine-h">
          <h2 id="spine-h" className="mb-4 text-xs uppercase tracking-[0.15em] text-muted-foreground">
            Browse the hymnal
          </h2>
          <SectionSpine sections={sectionsInScope} basePath={BASE} />
        </section>
      )}
    </>
  )
}
